import React, { useState } from "react";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
    Briefcase, Mail, Lock, LogIn, UserPlus,
    ChevronRight, ArrowLeft, Loader2, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const SellerLogin = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        password: ""
    });

    const [loginType, setLoginType] = useState('admin'); // 'admin' or 'cashier'
    const [posData, setPosData] = useState({ phone: "", pin: "" });
    const [posLoading, setPosLoading] = useState(false);

    const [showForgotModal, setShowForgotModal] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        try {
            const response = await axios.post(`${API}/sellers/forgot-password`, { email: forgotEmail });
            toast.success(response.data.message || "Tcheke imel ou pou lyen rekiperasyon an!");
            setShowForgotModal(false);
            setForgotEmail("");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Eksepte voye mesaj la echwe.");
        } finally {
            setForgotLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post(`${API}/sellers/login`, formData);
            if (response.data.success) {
                localStorage.setItem("seller_token", response.data.token);
                localStorage.setItem("seller_data", JSON.stringify(response.data.seller));
                sessionStorage.setItem("adminUnlocked", "true"); // Explicitly unlock for admin!
                toast.success("Byenvini nan Dashboard Machann ou!");
                navigate("/seller/dashboard");
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Imèl oswa modpas pa kòrèk.");
        } finally {
            setLoading(false);
        }
    };

    const handlePosSubmit = async (e) => {
        e.preventDefault();
        setPosLoading(true);
        try {
            const response = await axios.post(`${API}/sellers/pos-login`, posData);
            if (response.data.success) {
                localStorage.setItem("seller_token", response.data.token);
                localStorage.setItem("seller_data", JSON.stringify(response.data.seller));
                // Set the adminUnlocked to explicitly false so the POS loads the Kasye screen immediately
                sessionStorage.setItem("adminUnlocked", "false"); 
                toast.success("Koneksyon Kasye reyisi!");
                navigate("/seller/dashboard");
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Enfòmasyon ou yo pa kòrèk.");
        } finally {
            setPosLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />

            <Dialog open={showForgotModal} onOpenChange={setShowForgotModal}>
                <DialogContent className="sm:max-w-md rounded-[30px] p-8 border-none shadow-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-black uppercase tracking-tighter italic flex items-center">
                            <Lock className="w-6 h-6 mr-3 text-[#FF9900]" /> Bliye Modpas
                        </DialogTitle>
                        <DialogDescription className="text-gray-500 font-medium pt-2">
                            Antre imèl ou te itilize pou kreye magazen an, n ap voye yon lyen dirèk ba ou pou rekipere kont lan.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleForgotPassword} className="space-y-4 pt-4 mt-4 border-t border-gray-100">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Imèl Magazen</label>
                            <Input 
                                placeholder="machann@egzanp.com oswa id ou" 
                                type="text"
                                required
                                value={forgotEmail}
                                onChange={e => setForgotEmail(e.target.value)}
                                className="h-14 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={forgotLoading || !forgotEmail}
                            className="w-full h-14 bg-[#FF9900] hover:bg-black text-white font-black uppercase tracking-widest text-xs rounded-2xl transition-all shadow-xl shadow-orange-500/20 mt-4"
                        >
                            {forgotLoading ? <Loader2 className="animate-spin w-5 h-5 mx-auto" /> : "Voye Lyen Rekiperasyon"}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>

            <div className="flex-1 flex items-center justify-center px-4 py-12 bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-md w-full">
                    <div className="text-center mb-8">
                        <div className="w-20 h-20 bg-[#232F3E] rounded-[30px] flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-[#232F3E]/20">
                            <Briefcase className="w-10 h-10 text-[#FF9900]" />
                        </div>
                        <h1 className="text-3xl font-black text-gray-900 tracking-tighter mb-2 italic">Dashboard <span className="text-[#FF9900]">Machann</span></h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Espas administrasyon pou machann BVGLink sèlman.</p>
                    </div>

                    <Card className="rounded-[40px] border-none shadow-2xl overflow-hidden bg-white">
                        <CardHeader className="p-10 pb-2 text-center text-black">
                            <CardTitle className="text-xl font-black uppercase tracking-tight">Konekte kòm machann</CardTitle>
                            <CardDescription className="text-gray-400 font-medium">Antre enfòmasyon ou yo pou jere magazen ou.</CardDescription>
                        </CardHeader>
                        <CardContent className="p-10 pt-6">
                            <div className="flex bg-gray-100 rounded-2xl p-1 mb-8 shadow-inner">
                                <button
                                    type="button"
                                    onClick={() => setLoginType('admin')}
                                    className={`flex-1 flex items-center justify-center py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${loginType === 'admin' ? 'bg-white shadow-sm text-[#FF9900]' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                    <ShieldCheck className="w-4 h-4 mr-2" /> Mèt Magazen
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setLoginType('cashier')}
                                    className={`flex-1 flex items-center justify-center py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${loginType === 'cashier' ? 'bg-white shadow-sm text-[#FF9900]' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                    <Lock className="w-4 h-4 mr-2" /> Kasye POS
                                </button>
                            </div>

                            <form onSubmit={loginType === 'admin' ? handleSubmit : handlePosSubmit} className="space-y-6">
                                {loginType === 'admin' ? (
                                <>
                                    <div className="space-y-2 animate-in fade-in slide-in-from-left">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Imèl Machann</label>
                                        <div className="relative group">
                                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#FF9900] transition-colors" />
                                            <Input
                                                type="text"
                                                placeholder="admin, oubyen imèl ou"
                                                className="pl-12 h-14 rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-[#FF9900] font-bold text-black"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2 animate-in fade-in slide-in-from-left">
                                        <div className="flex justify-between items-center ml-1">
                                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Modpas</label>
                                            <button type="button" onClick={() => setShowForgotModal(true)} className="text-[10px] font-black uppercase tracking-widest text-[#FF9900] hover:underline">Bliye Modpas?</button>
                                        </div>
                                        <div className="relative group">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#FF9900] transition-colors" />
                                            <Input
                                                type="password"
                                                placeholder="••••••••"
                                                className="pl-12 h-14 rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-[#FF9900] font-bold text-black"
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                </>
                                ) : (
                                <>
                                    <div className="space-y-2 animate-in fade-in slide-in-from-right">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Imèl oswa Telefòn Magazen an</label>
                                        <div className="relative group">
                                            <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#FF9900] transition-colors" />
                                            <Input
                                                type="text"
                                                placeholder="Imèl ou oswa nimewo telefòn magazen an..."
                                                className="pl-12 h-14 rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-[#FF9900] font-bold text-black"
                                                value={posData.phone}
                                                onChange={(e) => setPosData({ ...posData, phone: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2 animate-in fade-in slide-in-from-right">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Kòd PIN Kesye a</label>
                                        <div className="relative group">
                                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#FF9900] transition-colors" />
                                            <Input
                                                type="password"
                                                maxLength="4"
                                                placeholder="••••"
                                                className="pl-12 h-14 rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-[#FF9900] font-bold text-black tracking-[1em] text-center"
                                                value={posData.pin}
                                                onChange={(e) => setPosData({ ...posData, pin: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                </>
                                )}

                                <Button
                                    type="submit"
                                    disabled={loading || posLoading}
                                    className="w-full bg-[#232F3E] hover:bg-black text-white h-16 rounded-[25px] font-black text-lg transition-all hover:scale-[1.02] shadow-xl shadow-[#232F3E]/20"
                                >
                                    {(loading || posLoading) ? <Loader2 className="animate-spin w-6 h-6" /> : <LogIn className="mr-3 w-6 h-6" />}
                                    KONEKTE KOUNYE A
                                </Button>
                            </form>

                            <div className="mt-10 pt-8 border-t border-gray-50 text-center">
                                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-4">Poko gen yon kont machann?</p>
                                <Link to="/seller/register">
                                    <Button variant="outline" className="w-full h-12 rounded-xl border-2 border-gray-100 font-black text-[#FF9900] hover:bg-gray-50">
                                        KREYE YON KONT MACHANN <ChevronRight className="ml-2 w-4 h-4" />
                                    </Button>
                                </Link>

                                <Link to="/marketplace" className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 mt-8 text-[10px] font-black uppercase tracking-widest transition-colors">
                                    <ArrowLeft className="w-3 h-3" /> Tounen nan Marketplace la
                                </Link>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="mt-8 flex items-center justify-center gap-4 py-4 px-6 bg-[#FF9900]/10 rounded-2xl border border-[#FF9900]/20">
                        <ShieldCheck className="w-5 h-5 text-[#FF9900]" />
                        <p className="text-[10px] font-black text-[#232F3E]/80 uppercase leading-snug">Sistèm nan separe. <span className="text-[#FF9900]">Kont sa a sèlman pou machann.</span> Kliyan yo pa ka antre isit la.</p>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};
